/**
 * ConnectBrokerageButton - CTA button to connect a brokerage account via Plaid.
 *
 * Styled as an outlined button to match "Add to portfolio" sizing with:
 * - Teal border and text (colors.petrolBlue)
 * - Wallet icon on the left
 * - Centered content
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { css } from '@emotion/react';
import { useTranslation } from 'react-i18next';
import { colors, fontStyles, spacing } from '../../styles';
import * as stores from '../../stores';
import useSubscriptionStatus from '../../hooks/useSubscriptionStatus';
import walletIcon from '../../assets/Icons/wallet.svg';
import PlaidLinkModal from './PlaidLinkModal';

interface ConnectBrokerageButtonProps {
  onConnected?: () => void | Promise<void>;
  isConnected?: boolean;
  institutionName?: string | null;
}

const ConnectBrokerageButton: React.FC<ConnectBrokerageButtonProps> = ({
  onConnected,
  isConnected = false,
  institutionName,
}) => {
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const isMountedRef = useRef(true);
  const isPurchaseInfoLoaded = stores.Purchase.useState((s) => s.isPurchaseInfoLoaded);
  const { isSubscribed } = useSubscriptionStatus();

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const handleClick = () => {
    // Block action until subscription info is loaded to prevent paywall bypass
    if (!isPurchaseInfoLoaded) {
      return;
    }
    if (!isSubscribed) {
      stores.showPaymentModalByPreference('plaid_connect_brokerage');
      return;
    }
    setIsModalOpen(true);
  };

  const handleClose = useCallback(() => {
    if (isMountedRef.current) {
      setIsModalOpen(false);
    }
  }, []);

  const handleSuccess = useCallback(async () => {
    // PlaidLinkModal shows "Syncing your holdings..." while this runs
    if (onConnected) {
      await onConnected();
    }
    if (isMountedRef.current) {
      setIsModalOpen(false);
    }
  }, [onConnected]);

  return (
    <>
      <button
        onClick={handleClick}
        css={css`
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: ${spacing.centi};
          padding: ${spacing.centi} ${spacing.regular};
          border: 1px solid ${colors.petrolBlue};
          border-radius: 4px;
          cursor: pointer;
          transition: all 0.2s ease;
          background-color: transparent;

          html[data-theme='dark'] & {
            background-color: transparent;
            border-color: ${colors.skyBlue};
          }

          &:hover {
            opacity: 0.8;
          }

          &:active {
            transform: scale(0.99);
          }
        `}
      >
        {/* Wallet icon */}
        <img src={walletIcon} alt="" width="20" height="20" />

        <span
          css={css`
            ${fontStyles.cat};
            font-weight: 500;
            color: ${colors.petrolBlue};
            html[data-theme='dark'] & {
              color: ${colors.skyBlue};
            }
          `}
        >
          {isConnected && institutionName
            ? t('portfolios.brokerage.reconnect', { institutionName })
            : t('portfolios.brokerage.connect')}
        </span>
      </button>

      <PlaidLinkModal isOpen={isModalOpen} onClose={handleClose} onSuccess={handleSuccess} />
    </>
  );
};

export default ConnectBrokerageButton;
